import { StringContext } from "../../core/StringContext";
import { StringData } from "../../core/StringData";
import { StringModule } from "../../core/StringModule";
import { StringObject } from "../../objects/StringObject";
import { frameDOM } from "../../utils/frame-dom";
import { isCoarsePointer } from "../../utils/isCoarsePointer";
import { styleTxn } from "../../utils/style-txn";

export abstract class CursorReactiveModule extends StringModule {
  protected nearOnly = true;
  protected useAllObjects = false;
  protected maxDistanceMultiplier = 1;
  protected updateThreshold = 0.1;

  protected enabled = true;

  constructor(context: StringContext) {
    super(context);
    if (isCoarsePointer()) {
      this.enabled = false;
    }
  }

  override onObjectConnected(object: StringObject): void {
    if (!this.enabled) return;
    super.onObjectConnected(object);
    this.centers.attach(object);
    this.hover.track(object);
  }

  override removeObject(id: string): void {
    if (!this.enabled) return super.removeObject(id);
    const obj = this.objectMapOnPage.get(id);
    if (obj) {
      this.centers.detach(obj);
      this.hover.untrack(obj);
    }
    super.removeObject(id);
  }

  override onScroll(): void {
    if (!this.enabled) return;
    this.centers.invalidateAll();
    this.scheduleCursorUpdate();
  }

  override onMouseMoveMeasure(data: StringData) {
    if (!this.enabled) return;
    super.onMouseMoveMeasure(data);
    this.refreshPointerState();
  }

  override onScrollMeasure(data: StringData) {
    if (!this.enabled) return;
    super.onScrollMeasure(data);
    this.refreshPointerState();
  }

  protected getCursorTargets(allowFallback = false): StringObject[] {
    if (!this.enabled) return [];
    const near = this.hover.activeObjects();
    if (this.nearOnly && near.length) return near;
    if (this.useAllObjects) return this.objectsOnPage;
    if (this.objects.length > 0) return this.objects;
    return allowFallback ? this.objectsOnPage : this.objects;
  }

  protected refreshPointerState(target?: StringObject, allowFallback = false): void {
    if (!this.enabled) return;

    const mx = this.data.cursor.targetX;
    const my = this.data.cursor.targetY;
    const targets = target ? [target] : this.getCursorTargets(allowFallback);

    const maxDistanceSq =
      !this.nearOnly && this.maxDistanceMultiplier > 0
        ? Math.pow(this.data.viewport.windowWidth * this.maxDistanceMultiplier, 2)
        : null;

    for (const obj of targets) {
      const { cx, cy } = this.centers.getCenter(obj);
      const dx = mx - cx;
      const dy = my - cy;
      const d2 = dx * dx + dy * dy;

      if (this.nearOnly) {
        const far = d2 > this.data.viewport.windowWidth * this.data.viewport.windowWidth;
        if (far && !this.hover.isActive(obj)) continue;
      } else if (maxDistanceSq !== null && d2 > maxDistanceSq) {
        continue;
      }

      obj.setProperty("dx", dx);
      obj.setProperty("dy", dy);
      obj.setProperty("dist", Math.sqrt(d2));
    }
  }

  private scrollUpdateScheduled = false;

  protected scheduleCursorUpdate(): void {
    if (!this.enabled || this.scrollUpdateScheduled) return;
    this.scrollUpdateScheduled = true;

    frameDOM.measure(() => {
      this.refreshPointerState();
      frameDOM.mutate(() => {
        this.scrollUpdateScheduled = false;
        styleTxn.run(() => {
          this.onCursorScrollUpdate();
        });
      });
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected onCursorScrollUpdate(): void {}
}
